import type { AqsiGood } from '../../catalog/model/types'

/** Строка выдачи поиска для продажи (`SaleSearchItemDto` Platform). */
export interface CommonSearchItem {
  productId: string
  name: string
  sku: string | null
  /** Штрихкод, по которому нашли (точное совпадение); иначе `null`. */
  matchedBarcode: string | null
  inCash: boolean
  /** Ключ карточки этого товара в кассе (карта идентичности); `null` — карточки в кассе ещё нет. */
  cardKey: string | null
}

export interface CommonSearchPage {
  items: readonly CommonSearchItem[]
  hasMore: boolean
}

/** Как искать: `barcode` — штрихкод целиком (скан), `text` — штрихкод/артикул целиком и текст единой службой поиска. */
export type CommonSearchMode = 'text' | 'barcode'

/**
 * Товар общего каталога и его карточка в кассе: `good` — карточка снимка кассы по ключу карты идентичности (цена, налог, единица для
 * продажи); `null` — карточки в кассе нет (товар «есть в каталоге, но ещё не в кассе») либо снимок кассы её ещё не отдал (тогда `cardKey` задан).
 */
export interface CommonGoodHit {
  productId: string
  name: string
  sku: string | null
  matchedBarcode: string | null
  cardKey: string | null
  good: AqsiGood | null
  deliveryReason: string | null
}

export interface CommonGoodsPage {
  hits: readonly CommonGoodHit[]
  hasMore: boolean
}

/** Причина, когда отправок товара в кассу не было: его можно отправить кнопкой. */
const NO_CARD_YET_TEXT = 'товара ещё нет в этой кассе'
/** Карточка у товара в кассе есть (карта идентичности), но снимок кабинета её ещё не отдал. */
const CARD_NOT_READ_TEXT = 'карточка в кассе заведена, но кабинет ещё не отдал её'

/** Почему товара нет в кассе: слова службы синхронизации (идёт отправка или отказ кассы дословно), иначе — что известно о карточке. */
export function absentReason(hit: CommonGoodHit): string {
  return hit.deliveryReason ?? (hit.cardKey === null ? NO_CARD_YET_TEXT : CARD_NOT_READ_TEXT)
}

/**
 * Ход «Отправить в кассу сейчас» (служба синхронизации, RP-SYN13): `pending` | `running` — создание карточки идёт, `succeeded` — карточка создана
 * и связана, `failed` — отказ кассы с её словами дословно.
 */
export interface CommonDeliverResult {
  jobId: string
  state: string
  code: string | null
  message: string | null
}

/** Состояния, при которых товар кассе отдан: ждём его карточку в кассе. */
export const DELIVERED_STATES: ReadonlySet<string> = new Set(['pending', 'running', 'succeeded'])

/** Почему товара нет в кассе — по товарам (служба синхронизации); товара нет в ответе — отправок не было. */
export type CommonDeliveryReasons = ReadonlyMap<string, string>

/** Предложение отправить товар в кассу (заметка РМК с кнопкой). */
export interface DeliveryOffer {
  productId: string
  name: string
  /** Слова причины дословно (очередь раздачи или отказ раздачи). */
  reason: string
  /** Штрихкод, по которому нашли: после раздачи товар добавляется в чек с ним. */
  barcode: string | null
  /** Что искал кассир и как (скан или строка): по этому поиску после раздачи ждём карточку товара в кассе. */
  text: string
  command: 'sale.scan' | 'sale.search'
  /** `offer` — кнопка; `sending` — запрос раздачи идёт; `waiting` — товар отдан кассе, ждём его карточку. */
  phase: 'offer' | 'sending' | 'waiting'
}
